import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import type { AuditLogRow } from '@/types/audit'
import { createClient } from '@/api/supabase'

export interface AuditListParams {
  page?: number
  pageSize?: number
  action?: string
  userId?: string
  dateFrom?: string
  dateTo?: string
}

export interface AuditListResult {
  items: AuditLogRow[]
  total: number
}

export async function listAudit(params: AuditListParams): Promise<AuditListResult> {
  if (USE_MOCK) {
    const page = Math.max(1, params.page ?? 1)
    const pageSize = Math.min(100, params.pageSize ?? 25)
    let rows = [...mockState.auditLog]
    if (params.action) rows = rows.filter((r) => r.action === params.action)
    if (params.userId) rows = rows.filter((r) => r.user_id === params.userId)
    if (params.dateFrom) {
      const t = new Date(params.dateFrom).getTime()
      rows = rows.filter((r) => new Date(r.created_at).getTime() >= t)
    }
    if (params.dateTo) {
      const t = new Date(params.dateTo).getTime()
      rows = rows.filter((r) => new Date(r.created_at).getTime() <= t)
    }
    rows.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    const total = rows.length
    const start = (page - 1) * pageSize
    return { items: rows.slice(start, start + pageSize), total }
  }

  const supabase = createClient()
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  // NB: без PostgREST-embed profiles(full_name) — FK audit_log.actor_id
  // ссылается на auth.users, а не на public.profiles, и embed падает
  // c PGRST200 «Could not find a relationship…». Вместо этого — двухшаговый
  // fetch: сначала строки аудита, затем имена по actor_id одним запросом.
  let query = supabase
    .from('audit_log')
    .select(
      'id, created_at, actor_id, action, entity_type, entity_id, payload',
      { count: 'exact' },
    )
    .order('created_at', { ascending: false })

  if (params.action) query = query.eq('action', params.action)
  if (params.userId) query = query.eq('actor_id', params.userId)
  if (params.dateFrom) query = query.gte('created_at', params.dateFrom)
  if (params.dateTo) query = query.lte('created_at', params.dateTo)

  const { data, error, count } = await query.range(from, to)
  if (error) throw new Error(error.message)

  const rows = (data ?? []) as Array<{
    id: string
    created_at: string
    actor_id: string | null
    action: string
    entity_type: string
    entity_id: string | null
    payload: Record<string, unknown> | null
  }>

  // Один запрос на имена актёров.
  const actorIds = Array.from(
    new Set(rows.map((r) => r.actor_id).filter((v): v is string => !!v)),
  )
  let nameById = new Map<string, string>()
  if (actorIds.length > 0) {
    const { data: profs, error: profErr } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', actorIds)
    if (profErr) throw new Error(profErr.message)
    nameById = new Map(
      (profs ?? []).map((p) => [p.id as string, (p.full_name as string | null) ?? '']),
    )
  }

  const items: AuditLogRow[] = rows.map((row) => ({
    id: row.id,
    created_at: row.created_at,
    user_id: row.actor_id ?? '',
    user_name: row.actor_id ? nameById.get(row.actor_id) ?? null : null,
    action: row.action,
    object_type: row.entity_type,
    object_id: row.entity_id ?? '',
    details: row.payload ?? {},
  }))

  return { items, total: count ?? 0 }
}
